import { Info } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { cn } from '@/lib/utils';

interface Breakdown {
  team1: number;
  team2: number;
  base?: { team1_avg: number; team2_avg: number; team1_pct: number };
  chemistry?: {
    team1: { played: number; won: number; boost: number };
    team2: { played: number; won: number; boost: number };
  };
  h2h?: { played: number; team1_wins: number; shift_pct: number };
}

interface Props {
  team1: number | null | undefined;
  team2: number | null | undefined;
  breakdown?: Breakdown | null;
}

const pairText = (p?: { played: number; won: number }) =>
  !p || p.played === 0 ? 'No matches together yet' : `${p.won}/${p.played} won together`;

export function MatchWinProbability({ team1, team2, breakdown }: Props) {
  if (team1 == null || team2 == null) return null;
  const t1 = Math.round(Number(team1));
  const t2 = 100 - t1;
  const even = Math.abs(t1 - t2) <= 4;
  const b = breakdown;

  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          onClick={(e) => e.stopPropagation()}
          className="w-full min-h-11 px-1 py-1.5 text-left rounded-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          aria-label={`Win chance: Team 1 ${t1}%, Team 2 ${t2}%. Tap for details`}
        >
          <div className="flex items-center justify-between text-xs mb-1">
            <span className={cn('font-bold tabular-nums', t1 >= t2 ? 'text-primary' : 'text-muted-foreground')}>{t1}%</span>
            <span className="flex items-center gap-1 text-muted-foreground">
              {even ? 'Even matchup' : 'Win chance'} <Info className="h-3 w-3" />
            </span>
            <span className={cn('font-bold tabular-nums', t2 > t1 ? 'text-foreground' : 'text-muted-foreground')}>{t2}%</span>
          </div>
          <div className="relative h-1.5 w-full rounded-full overflow-hidden bg-muted flex">
            <div className="h-full bg-primary shadow-[0_0_8px_hsl(var(--primary)/0.6)] transition-all duration-700" style={{ width: `${t1}%` }} />
            <div className="h-full bg-muted-foreground/50 flex-1" />
            <div className="absolute inset-0 win-prob-shine pointer-events-none" />
          </div>
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-72 text-sm space-y-3" onClick={(e) => e.stopPropagation()}>
        <div className="font-semibold">Matchup breakdown</div>
        <div>
          <div className="flex justify-between text-xs text-muted-foreground"><span>Skill rating</span><span>60%</span></div>
          <div className="text-xs">Team averages {b?.base?.team1_avg ?? '–'} vs {b?.base?.team2_avg ?? '–'} → {Math.round(b?.base?.team1_pct ?? t1)}% / {100 - Math.round(b?.base?.team1_pct ?? t1)}%</div>
        </div>
        <div>
          <div className="flex justify-between text-xs text-muted-foreground"><span>Team chemistry</span><span>25%</span></div>
          <div className="text-xs">Team 1: {pairText(b?.chemistry?.team1)}</div>
          <div className="text-xs">Team 2: {pairText(b?.chemistry?.team2)}</div>
        </div>
        <div>
          <div className="flex justify-between text-xs text-muted-foreground"><span>Head-to-head</span><span>15%</span></div>
          <div className="text-xs">
            {b?.h2h?.played ? `Team 1 won ${b.h2h.team1_wins} of ${b.h2h.played} previous meetings` : 'These teams have not met before'}
          </div>
        </div>
        <p className="text-xs text-muted-foreground border-t border-border pt-2">
          Padel insight: a forecast, not a result. Strong partnerships often beat higher ratings.
        </p>
      </PopoverContent>
    </Popover>
  );
}
